import { useState, useMemo } from 'react';
import { Loader2, DollarSign, Hash, Cpu, Phone, Users, Clock, Activity } from 'lucide-react';
import { useUsageSummary, useUsageByAssistant, useUsageByUser } from '@/hooks/useAssistantUsage';
import type { AssistantUsageItem, UserUsageItem } from '@/hooks/useAssistantUsage';
import {
  useVoiceUsageSummary,
  useVoiceUsageByType,
  useVoiceUsageByUser,
} from '@/hooks/useVoiceUsage';
import type { SessionTypeUsageItem, VoiceUserUsageItem } from '@/hooks/useVoiceUsage';

type UsageTab = 'ai' | 'voice';

const SESSION_TYPE_LABELS: Record<string, string> = {
  channel: 'Канал',
  direct: 'Директно',
  group: 'Група',
  voice_room: 'Гласова стая',
};

const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const daysAgo = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return formatDate(d);
};

const formatMinutes = (minutes: number): string => {
  if (minutes < 60) return `${minutes.toFixed(1)} мин`;
  const hours = Math.floor(minutes / 60);
  const remaining = Math.round(minutes % 60);
  return remaining > 0 ? `${hours}ч ${remaining}м` : `${hours}ч`;
};

export const AssistantUsageDashboard = () => {
  const [activeTab, setActiveTab] = useState<UsageTab>('ai');
  const [dateFrom, setDateFrom] = useState<string>(daysAgo(30));
  const [dateTo, setDateTo] = useState<string>(formatDate(new Date()));

  const isAiTab = activeTab === 'ai';
  const isVoiceTab = activeTab === 'voice';

  // AI usage hooks (only fetch when AI tab active)
  const { data: summary, isLoading: summaryLoading } = useUsageSummary(dateFrom, dateTo, isAiTab);
  const { data: byAssistant, isLoading: assistantLoading } = useUsageByAssistant(dateFrom, dateTo, isAiTab);
  const { data: byUser, isLoading: userLoading } = useUsageByUser(dateFrom, dateTo, isAiTab);

  // Voice usage hooks (only fetch when Voice tab active)
  const { data: voiceSummary, isLoading: voiceSummaryLoading } = useVoiceUsageSummary(dateFrom, dateTo, isVoiceTab);
  const { data: voiceByType, isLoading: voiceTypeLoading } = useVoiceUsageByType(dateFrom, dateTo, isVoiceTab);
  const { data: voiceByUser, isLoading: voiceUserLoading } = useVoiceUsageByUser(dateFrom, dateTo, isVoiceTab);

  const aiLoading = summaryLoading || assistantLoading || userLoading;
  const voiceLoading = voiceSummaryLoading || voiceTypeLoading || voiceUserLoading;
  const isLoading = isAiTab ? aiLoading : voiceLoading;

  const sortedAssistants = useMemo(
    () => byAssistant ? [...byAssistant].sort((a, b) => b.costUsd - a.costUsd) : [],
    [byAssistant],
  );

  const sortedUsers = useMemo(
    () => byUser ? [...byUser].sort((a, b) => b.costUsd - a.costUsd) : [],
    [byUser],
  );

  const sortedVoiceUsers = useMemo(
    () => voiceByUser ? [...voiceByUser].sort((a, b) => b.totalMinutes - a.totalMinutes) : [],
    [voiceByUser],
  );

  const setQuickRange = (days: number | null) => {
    if (days === null) {
      setDateFrom('');
      setDateTo('');
    } else {
      setDateFrom(daysAgo(days));
      setDateTo(formatDate(new Date()));
    }
  };

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-dark-text-secondary">От</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-sm text-gray-900 dark:text-dark-text-primary"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-dark-text-secondary">До</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-sm text-gray-900 dark:text-dark-text-primary"
          />
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setQuickRange(7)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-dark-border-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover text-gray-700 dark:text-dark-text-secondary"
          >
            7 дни
          </button>
          <button
            onClick={() => setQuickRange(30)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-dark-border-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover text-gray-700 dark:text-dark-text-secondary"
          >
            30 дни
          </button>
          <button
            onClick={() => setQuickRange(null)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-dark-border-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover text-gray-700 dark:text-dark-text-secondary"
          >
            Всичко
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-dark-border-primary">
        <button
          onClick={() => setActiveTab('ai')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            isAiTab
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
          }`}
        >
          <Cpu className="w-4 h-4" />
          AI Асистенти
        </button>
        <button
          onClick={() => setActiveTab('voice')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            isVoiceTab
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
          }`}
        >
          <Phone className="w-4 h-4" />
          Гласови обаждания
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* === AI TAB === */}
          {isAiTab && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Hash className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                      Заявки
                    </span>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                    {(summary?.total_requests ?? 0).toLocaleString('bg-BG')}
                  </p>
                </div>

                <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Cpu className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                      Токени
                    </span>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                    {(summary?.total_tokens ?? 0).toLocaleString('bg-BG')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                    Вход: {(summary?.total_prompt_tokens ?? 0).toLocaleString('bg-BG')} / Изход: {(summary?.total_completion_tokens ?? 0).toLocaleString('bg-BG')}
                  </p>
                </div>

                <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <span className="text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                      Разход
                    </span>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                    ${(summary?.total_cost_usd ?? 0).toFixed(4)}
                  </p>
                </div>
              </div>

              {/* Per-Assistant Table */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-3">
                  Употреба по асистент
                </h3>
                {sortedAssistants.length > 0 ? (
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-dark-bg-hover border-b border-gray-200 dark:border-dark-border-primary">
                          <th className="text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Асистент
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Заявки
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Токени
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Разход ($)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-dark-border-primary">
                        {sortedAssistants.map((item: AssistantUsageItem) => (
                          <tr key={item.assistantId} className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover">
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                                @{item.slug}
                              </span>
                              {item.name && (
                                <span className="ml-2 text-xs text-gray-500 dark:text-dark-text-muted">
                                  {item.name}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-dark-text-secondary">
                              {item.requests.toLocaleString('bg-BG')}
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-dark-text-secondary">
                              {item.totalTokens.toLocaleString('bg-BG')}
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                              ${item.costUsd.toFixed(4)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 dark:text-dark-text-muted text-sm">
                    Няма данни за избрания период
                  </div>
                )}
              </div>

              {/* Per-User Table */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-3">
                  Употреба по потребител
                </h3>
                {sortedUsers.length > 0 ? (
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-dark-bg-hover border-b border-gray-200 dark:border-dark-border-primary">
                          <th className="text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Потребител
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Заявки
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Токени
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Разход ($)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-dark-border-primary">
                        {sortedUsers.map((item: UserUsageItem) => (
                          <tr key={item.userId} className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover">
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                                  {item.displayName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                                  {item.email}
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-dark-text-secondary">
                              {item.requests.toLocaleString('bg-BG')}
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-dark-text-secondary">
                              {item.totalTokens.toLocaleString('bg-BG')}
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                              ${item.costUsd.toFixed(4)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 dark:text-dark-text-muted text-sm">
                    Няма данни за избрания период
                  </div>
                )}
              </div>
            </>
          )}

          {/* === VOICE TAB === */}
          {isVoiceTab && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Phone className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                      Общо сесии
                    </span>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                    {(voiceSummary?.total_sessions ?? 0).toLocaleString('bg-BG')}
                  </p>
                </div>

                <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                      Общо минути
                    </span>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                    {formatMinutes(voiceSummary?.total_minutes ?? 0)}
                  </p>
                </div>

                <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-green-500" />
                    <span className="text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                      Участник-минути
                    </span>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                    {formatMinutes(voiceSummary?.total_participant_minutes ?? 0)}
                  </p>
                </div>

                <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4 text-orange-500" />
                    <span className="text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                      Активни сесии
                    </span>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                    {(voiceSummary?.active_sessions ?? 0).toLocaleString('bg-BG')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                    {(voiceSummary?.unique_participants ?? 0).toLocaleString('bg-BG')} уникални участници
                  </p>
                </div>
              </div>

              {/* By Session Type Table */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-3">
                  Употреба по тип сесия
                </h3>
                {voiceByType && voiceByType.length > 0 ? (
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-dark-bg-hover border-b border-gray-200 dark:border-dark-border-primary">
                          <th className="text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Тип
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Сесии
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Минути
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Участници
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-dark-border-primary">
                        {voiceByType.map((item: SessionTypeUsageItem) => (
                          <tr key={item.sessionType} className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                              {SESSION_TYPE_LABELS[item.sessionType] ?? item.sessionType}
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-dark-text-secondary">
                              {item.sessions.toLocaleString('bg-BG')}
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-dark-text-secondary">
                              {formatMinutes(item.totalMinutes)}
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-dark-text-secondary">
                              {item.participants.toLocaleString('bg-BG')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 dark:text-dark-text-muted text-sm">
                    Няма данни за избрания период
                  </div>
                )}
              </div>

              {/* By User Table */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-3">
                  Употреба по потребител
                </h3>
                {sortedVoiceUsers.length > 0 ? (
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-dark-bg-hover border-b border-gray-200 dark:border-dark-border-primary">
                          <th className="text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Потребител
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Сесии
                          </th>
                          <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">
                            Общо минути
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-dark-border-primary">
                        {sortedVoiceUsers.map((item: VoiceUserUsageItem) => (
                          <tr key={item.userId} className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover">
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                                  {item.displayName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                                  {item.email}
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-dark-text-secondary">
                              {item.sessionCount.toLocaleString('bg-BG')}
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                              {formatMinutes(item.totalMinutes)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 dark:text-dark-text-muted text-sm">
                    Няма данни за избрания период
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
